import { SelectButton } from "primereact/selectbutton";
import React from "react";
import { RangeButtons } from "./RangeButtons.tsx";
import { DoubleDigitNumbers, Operations, TripleDigitNumbers } from "../Constants.ts";
import { DefaultSettings } from "../Models.ts";
import { bucketFor, isBucketOfTens, operationsOf } from "../FactRanges.ts";
import { CheckBoxes } from "./Checkboxes.tsx";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import 'primeicons/primeicons.css';
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

/**
 * The digit-count picker. A tick in the row below changes the selection and
 * not the digit count, so this is its own memoised component and a tick does
 * not redraw it (ADR 0081).
 */
const DigitCount = React.memo(function DigitCount({rangeId, digits, isLongDivision}: {rangeId: number, digits: number, isLongDivision: boolean}) {
    const {getSettings, setSettings} = useSettingsActions();
    const t = useTranslation();
    function handleDigitSelection(value, rangeGroup) {
        const settings = getSettings();
        let updatedSettings = JSON.parse(JSON.stringify(settings)); 
        let range = updatedSettings.NumberRanges.filter(x => x.Id === rangeGroup)[0];
        range.NumberOfDigits = value;
        const defaultSettings = JSON.parse(JSON.stringify(DefaultSettings));
        const defaultRange = defaultSettings.NumberRanges.filter(x => x.Id === rangeGroup)[0].NumberRangeSelection;        
        switch(value) {
            case 1: {
                // 0-12 on a multiplication/division sheet, 0-9 otherwise; the
                // default selection stays inside 0-9 either way (ADR 0064 §2).
                range.NumberRange = [...bucketFor(1, operationsOf(updatedSettings)).Range];
                range.NumberRangeSelection = defaultRange;
                // if (rangeGroup === 1 && isLongDivision) {
                //     const longDivisionRange = SingleDigitNumbers.Range.map(x => x);
                //     longDivisionRange.shift();
                //     range.NumberRange = longDivisionRange;
                // }
                break;
            }
            case 2: {
                range.NumberRange = DoubleDigitNumbers.Range;
                if (rangeGroup === 1) {
                    range.NumberRangeSelection =  [DoubleDigitNumbers.Range[4]]
                 } else {
                    range.NumberRangeSelection =  DoubleDigitNumbers.Range
                 }   
                break;
            }
            case 3: {
                range.NumberRange = TripleDigitNumbers.Range;
                if (rangeGroup === 1) {
                    range.NumberRangeSelection =  [TripleDigitNumbers.Range[4]]
                 } else {
                    range.NumberRangeSelection =  TripleDigitNumbers.Range
                 }   
                break;         
            }
        }
        setSettings(updatedSettings);
    }
    const digitOptions = isLongDivision ? LONG_DIVISION_DIGITS : ALL_DIGITS;
    return (
        <div className='selectContainer'>
            {/* A span, not a label: SelectButton is a group of buttons rather
                than one input, so it is named with aria-label instead. */}
            <span className='settingLabel'>{t('workbench.digits')}</span>
            <SelectButton
                value={digits}
                onChange={(e) => handleDigitSelection(e.value, rangeId)}
                options={ digitOptions }
                className='select'
                aria-label={format(t('settings.numberOfDigitsForRangeAria'), { id: rangeId })}
            />
        </div>
    );
});

const ALL_DIGITS = [1, 2, 3];
const LONG_DIVISION_DIGITS = [1, 2];

export const ValueRange = React.memo(function ValueRange({RangeSettings}: {RangeSettings: number}) {
    const {range, isLongDivision} = useSetting((settings) => ({
        range: settings.NumberRanges[RangeSettings],
        isLongDivision: settings.Operators?.findIndex(x => x.value === Operations.LongDivision) >= 0,
    }));
    const t = useTranslation();
    const groupLabelId = `valueRangeLabel${range.Id}`;
    return (
        <div key={range.Id} className='valueRange' role='group' aria-labelledby={groupLabelId}>
            {/* A kicker plus a one-line summary of what is picked (ADR 0061
                §3), so a collapsed or scrolled rail still says what the sheet
                draws from. */}
            <div className='valueRangeHead'>
                <div id={groupLabelId} className='kicker'>
                    {range.Id === 1 ? t('workbench.firstNumber') : t('workbench.secondNumber')}
                </div>
                <span className='railSummary'>
                    {selectionSummary(range.NumberRange, range.NumberRangeSelection, t)}
                </span>
            </div>
            <DigitCount rangeId={range.Id} digits={range.NumberOfDigits} isLongDivision={isLongDivision}/>
            <div id={'numberRangeOptions'+range.Id} className='numberRangeOptions'>
                <div className='checkboxContainer' role='group'
                     aria-label={format(t('settings.numbersToUseInRangeAria'), { id: range.Id })}>
                    <CheckBoxes
                        numberRange={range.NumberRange}
                        numberSelection={range.NumberRangeSelection}
                        rangeGroup={range.Id}/>
                </div>
                <RangeButtons rangeGroup={range.Id} range={range.NumberRange}/>
            </div>
        </div>
    );
});

/**
 * "all", "none picked", the values themselves when there are few enough to
 * read at a glance ("5", "2, 5, 7", "10s, 20s"), or a count.
 */
export function selectionSummary(
    numberRange: number[],
    selection: number[],
    t: (key: any) => string
): string {
    if (selection.length === 0) {
        return t('workbench.summaryNone');
    }
    if (numberRange.every((value) => selection.includes(value))) {
        return t('workbench.summaryAll');
    }
    if (selection.length <= 3) {
        return [...selection]
            .sort((a, b) => a - b)
            .map((value) => (isBucketOfTens(numberRange) ? `${value}s` : String(value)))
            .join(', ');
    }
    return format(t('workbench.summaryMany'), { n: selection.length });
}
